import React, { useState, useEffect, useContext, useRef } from "react";
import { DeviceContract, TreeNodeModel } from "../tree/types";
import {
  ClassifierDeviceForm,
  DeviceClassifierRow
} from "./ClassifierDeviceForm";
import {
  AdditionalDeviceParamsForm,
  AdditionalDeviceParams
} from "./AdditionalDeviceParamsForm";
import { AuthContext } from "../../context/auth";
import Message from "../ui/Message";
import { ApplicationsContext } from "../../context/applications/ApplicationContext";
import { Classifier, Tariff } from "../../models/interfaces";
import { DeviceTariffForm } from "./TariffForm";
import { LimitRow, LimitsForm } from "./LimitForm";

interface Props {
  node: TreeNodeModel;
  onSave: (node: TreeNodeModel, terminal: any) => void;
  onChange: (data: any) => void;
  readOnly?: boolean;
  forceAdjustLimits?: boolean;
  isPayfacReq?: boolean;
  filterPlans?: boolean
}

export interface SettlementParams {
  localCurrencyAccountNumber: string;
  usdAccountNumber: string;
  tariffPlan: string;
}

const lafiseContractNames = [
  "POS-BeePay",
  "POS-Ingenico",
  "POS-Verifone",
  "LP-BeePay",
  "API-BeePay",
  "WEB-BeePay",
  "LP-Poket",
  "QR-BeePay",
  "QR-Poket",
  "QR-Integración",
  "T2P-Poket",
  "Cargos Recurrentes",
  "PinPad-Laro",
  "PinPad-CNET",
  "PinPad",
  "ECR-Laro",
  "ECR-CNET",
  "ECR",
  "P2P"
];

export interface DeviceTariffRow {
  tariffCode: string;
  dateFrom: string;
  feeBase: string;
  feeMin: string;
  feeMax: string;
  feePcnt: string;
  customize?: boolean;

}

interface TerminalFormData {
  product: string;
  contractName: string;

  deviceCategory: string;
  deviceType: string;
  deviceStatus: string;
  timeOffset: string;
  defaultCurrency: string;

  threeDS?: "Y" | "N";
  externalMPI?: "Y" | "N";
  mpiUrl?: string;

  macStatus: string;
  pbtStatus: string;

  deviceClassifiers: DeviceClassifierRow[];
  additionalParams?: AdditionalDeviceParams;
  settlementParams: SettlementParams;
  adjustTariffs?: "Y" | "N";
  deviceTariffs: DeviceTariffRow[];
  adjustLimits?: "Y" | "N";
  deviceLimits: LimitRow[];
  statusCode?: string;
  terminalId?: string;
}

export const TerminalForm: React.FC<Props> = ({ node, onSave, readOnly, onChange,
  forceAdjustLimits, isPayfacReq = false, filterPlans = false }) => {
  const { user } = useContext(AuthContext);
  const isUserPayfac = user.groups[0].is_payfac_group == 'Y';
  const { getTariffPlan, getClassifiers } = useContext(ApplicationsContext);

  const [errorMessage, setErrorMessage] = useState("");
  const [tariffs, setTariffs] = useState<Tariff[]>([]);
  const [classifiers, setClassifiers] = useState<Classifier[]>([]);
  const loadingRef = useRef(true);



  const [form, setForm] = useState<TerminalFormData>({
    product: "",
    contractName: "",
    deviceCategory: "",
    defaultCurrency: user.country.code == 'PAN' ? "USD" : "CRC",
    deviceType: "",
    deviceStatus: "00",
    timeOffset: "1",
    macStatus: "O",
    pbtStatus: "O",
    threeDS: "N",
    externalMPI: "N",
    mpiUrl: "https://lafise.com",
    deviceClassifiers: [],
    deviceTariffs: [],
    adjustTariffs: "N",
    additionalParams: undefined,
    settlementParams: {
      localCurrencyAccountNumber: "",
      usdAccountNumber: "",
      tariffPlan: ""
    },
    terminalId: "",
    adjustLimits: "N",
    deviceLimits: []
  });

  useEffect(() => {
    getTariffPlan().then(setTariffs);
    getClassifiers().then(setClassifiers);
  }, []);


  useEffect(() => {

    if (classifiers.length === 0) return;

    loadingRef.current = true;

    if ((node as any).entityData) {

      const data = (node as any).entityData as TerminalFormData;

      setForm({
        ...data,
        deviceClassifiers: data.deviceClassifiers || [],
        ...(forceAdjustLimits !== undefined && {
          adjustLimits: forceAdjustLimits ? "Y" : "N"
        })
      });

    } else {

      let countryCode = user.country.code;
      let defaultTimeOffset = "1";

      if (countryCode === "PAN") defaultTimeOffset = "1";
      else if (countryCode === "CRC") defaultTimeOffset = "0";

      setForm(prev => ({
        ...prev,
        timeOffset: defaultTimeOffset,
        adjustLimits: forceAdjustLimits ? "Y" : "N"
      }));
    }

    setTimeout(() => {
      loadingRef.current = false;
    }, 50);

  }, [node, classifiers]);

  const removeError = () => setErrorMessage("");

  const updateLive = (newForm: TerminalFormData) => {

    setForm(newForm);

    if (loadingRef.current) return;
    if (!onChange) return;

    onChange({
      ...node,
      name: newForm.contractName,
      entityData: newForm
    });
  };

  const handleProductChange = (product: string) => {
    let deviceCategory = "";
    let deviceType = "";

    switch (product) {
      case "052-Acq POS level":
        deviceCategory = "POS";
        deviceType = "PC-POS";
        break;
      case "052-Acq MPOS level":
        deviceCategory = "POS";
        deviceType = "Device H2H";
        break;
      case "052-Acq ECOM level":
        deviceCategory = "ECOMM";
        deviceType = "Ecommerce POS";
        break;
      case "052-Acq QR level":
        deviceCategory = "POS";
        deviceType = "Device H2H";
        break;
    }

    const newForm = {
      ...form,
      product,
      deviceCategory,
      deviceType
    };

    updateLive(newForm);
  };

  const isBank0051 = user?.banks?.[0]?.code === '0051';
  let filteredTariffs = tariffs;
  if (filterPlans && isBank0051) {
    filteredTariffs = tariffs.filter(t =>
      isPayfacReq
        ? !t.valueCode.includes("TARIFF_PLAN")
        : t.valueCode.includes("TARIFF_PLAN")
    )
  }

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;



    if (name === "adjustLimits") return;

    const newForm = {
      ...form,
      [name]: value
    };

    updateLive(newForm);
  };

  const handleSave = () => {
    if (readOnly) return;

    const OPTIONAL_CLASSIFIERS = [
      "INTRA_PLAN",
      "INTRA_TASA",
      "PUNTOS_TASA"
    ];

    const missingClassifiers =
      form.deviceClassifiers?.some(
        c =>
          !c.value &&
          !OPTIONAL_CLASSIFIERS.some(
            optionalCode => c.code.includes(optionalCode)
          )
      );

    if (
      !form.product ||
      !form.contractName ||
      (
        !form.settlementParams.localCurrencyAccountNumber &&
        !form.settlementParams.usdAccountNumber
      ) ||
      (!isUserPayfac && !form.settlementParams.tariffPlan) ||
      missingClassifiers
    ) {
      setErrorMessage("Por favor complete todos los campos requeridos.");
      return;
    }

    if (form.adjustLimits === "Y") {
      const hasInvalidLimit = form.deviceLimits.some(limit => {
        const hasMaxAmount = limit.maxAmount !== undefined && limit.maxAmount !== "";
        const hasMaxSingleAmount = limit.maxSingleAmount !== undefined && limit.maxSingleAmount !== "";
        const hasMaxNumber = limit.maxNumber !== undefined && limit.maxNumber !== "";

        // ❌ valores negativos
        if (hasMaxAmount && parseFloat(limit.maxAmount) < 0) return true;
        if (hasMaxSingleAmount && parseFloat(limit.maxSingleAmount) < 0) return true;
        if (hasMaxNumber && parseInt(limit.maxNumber) < 0) return true;

        // ❌ ninguno de los 3 viene lleno
        if (!hasMaxAmount && !hasMaxSingleAmount && !hasMaxNumber) return true;

        return false;
      });

      if (hasInvalidLimit) {
        setErrorMessage("Por favor ingrese valores válidos para los límites.");
        return;
      }
    }

    if (form.adjustTariffs === "Y") {

      const hasInvalidTariff = form.deviceTariffs?.some(t =>
        t.customize === true &&
        !t.feePcnt?.trim()
      );

      if (hasInvalidTariff) {
        setErrorMessage("Por favor ingrese valores válidos para las tarifas.");
        return;
      }

    }

    const updatedNode: TreeNodeModel = {
      ...node,
      name: form.contractName,
      saved: true,
      entityData: form
    } as any;

    onSave(updatedNode, form);
  };

  return (
    <div className="container-fluid">
      <h5 className="mb-3">Terminal / Dispositivo</h5>

      {/* Producto */}
      <div className="mb-3">
        <label className="form-label">Producto *</label>
        <select
          className="form-select"
          value={form.product}
          onChange={e => handleProductChange(e.target.value)}
          disabled={readOnly || node.isExistent}
        >
          <option value="">Seleccione producto</option>
          <option value="052-Acq POS level">POS</option>

          {user.isLafise && <option value="052-Acq ECOM level">ECOM</option>}
          {!user.isLafise && <option value="052-Acq MPOS level">Tap On Phone</option>}

        </select>
      </div>
      <div className="row">
        <div className={form.terminalId ? "col-md-6 mb-3" : "col-md-12 mb-3"}>

          <label className="form-label">Nombre del Terminal *</label>

          {user?.isLafise ? (
            <select
              name="contractName"
              className="form-select"
              value={form.contractName}
              onChange={handleChange}
              disabled={readOnly || node.isExistent}
            >
              <option value="">Seleccione</option>
              {lafiseContractNames.map((name, i) => (
                <option key={i} value={name}>
                  {name}
                </option>
              ))}
            </select>
          ) : (
            <input
              name="contractName"
              className="form-control"
              value={form.contractName}
              onChange={handleChange}
              disabled={readOnly}
            />
          )}
        </div>


        {form.terminalId && <div className="col-md-6 mb-3">
          <label className="form-label">Terminal Id</label>

          {(
            <input
              name="contractName"
              className="form-control"
              value={form.terminalId}
              onChange={handleChange}
              disabled={true}

            />
          )}
        </div>}
      </div>

      <hr />

      <h6>Configuración del Dispositivo</h6>
      <div className="row">
        <div className="col-md-6 mb-3">
          <label className="form-label">Categoría del Equipo</label>
          <input className="form-control" value={form.deviceCategory} disabled />
        </div>

        <div className="col-md-6 mb-3">
          <label className="form-label">Tipo de Equipo</label>
          {form.product === "052-Acq POS level" ? (
            <select
              name="deviceType"
              className="form-select"
              value={form.deviceType}
              onChange={handleChange}
              disabled={readOnly}
            >
              <option value="PC-POS">PC-POS</option>
              <option value="POS-HOTEL">POS Hotel</option>
            </select>
          ) : (
            <input className="form-control" value={form.deviceType} disabled />
          )}
        </div>
        {form.product === "052-Acq ECOM level" && (
          <>
            <div className="col-md-4 mb-3">
              <label className="form-label">Aplica 3DS</label>
              <select
                name="threeDS"
                className="form-select"
                value={form.threeDS || "N"}
                onChange={handleChange}
                disabled={readOnly}
              >
                <option value="N">No</option>
                <option value="Y">Sí</option>
              </select>
            </div>

            <div className="col-md-4 mb-3">
              <label className="form-label">MPI Externo</label>
              <select
                name="externalMPI"
                className="form-select"
                value={form.externalMPI || "N"}
                onChange={handleChange}
                disabled={readOnly || form.threeDS === "N"}
              >
                <option value="N">No</option>
                <option value="Y">Sí</option>
              </select>
            </div>

            <div className="col-md-4 mb-3">
              <label className="form-label">URL MPI</label>
              <input
                name="mpiUrl"
                className="form-control"
                value={form.mpiUrl || ""}
                onChange={handleChange}
                disabled={readOnly}
              />
            </div>
          </>
        )}

        {node.isExistent && <div className="col-md-6 mb-3">
          <label className="form-label">Estado del Dispositivo</label>

          <select
            name="statusCode"
            className="form-select"
            value={form.statusCode}
            onChange={handleChange}
            disabled={readOnly || (node.originalEntityData as DeviceContract).deviceStatus === "102"}
          >
            <option value="00">Ok</option>
            <option value="58">Para cerrar</option>
            <option value="14">Cerrado</option>
            <option value="114">Bloqueado</option>
          </select>
        </div>}
      </div>



      {
        form.deviceType && (<> <hr /><ClassifierDeviceForm
          key={"classifier" + node.id}
          classifiers={classifiers}
          data={form.deviceClassifiers}
          deviceType={form.deviceType}
          readonly={readOnly}
          onChange={data => {
            const newForm = {
              ...form,
              deviceClassifiers: data
            };

            updateLive(newForm);
          }
          }
        /></>)
      }

      {<>


        <AdditionalDeviceParamsForm

          key={"additional" + node.id}
          data={form.additionalParams}
          readonly={readOnly}
          deviceType={form.product}
          onChange={data => {
            const newForm = {
              ...form,
              additionalParams: data
            };
            updateLive(newForm);
          }
          }

        />
      </>}

      <hr />

      <h6>Parámetros de Liquidación</h6>
      <div className="row">
        <div className="col-md-4 mb-3">
          <label className="form-label">Cuenta Liquidadora </label>
          <input
            className="form-control"
            value={form.settlementParams.localCurrencyAccountNumber}
            onChange={e => {
              const newForm = {
                ...form,
                settlementParams: {
                  ...form.settlementParams,
                  localCurrencyAccountNumber: e.target.value.trim()
                }
              };

              setForm(newForm);
              updateLive(newForm);
            }
            }

            disabled={readOnly}
          />
        </div>

        {user.isLafise && user.country.code == 'CRI' && <div className="col-md-4 mb-3">
          <label className="form-label">Cuenta Liquidadora - USD</label>
          <input
            className="form-control"
            value={form.settlementParams.usdAccountNumber}

            onChange={e => {
              const newForm = {
                ...form,
                settlementParams: {
                  ...form.settlementParams,
                  usdAccountNumber: e.target.value.trim()
                }
              };

              updateLive(newForm);
            }
            }
            disabled={readOnly}
          />
        </div>}

        {!isUserPayfac && <div className="col-md-4 mb-3">
          <label className="form-label">Plan de Tarifa *</label>
          <select
            className="form-select"
            value={form.settlementParams.tariffPlan}

            onChange={e => {
              const newForm = {
                ...form,
                settlementParams: {
                  ...form.settlementParams,
                  tariffPlan: e.target.value
                }
              };

              updateLive(newForm);
            }
            }
            disabled={readOnly}
          >
            <option value="">Seleccione</option>
            {filteredTariffs.map((t, i) => (
              <option key={t.classifierId + i} value={t.valueCode}>
                {t.valueName}
              </option>
            ))}
          </select>
        </div>}

        {
          !isUserPayfac && (
            <>

              <hr />
              <h6 className="mb-3">Tarifas del Dispositivo</h6>
              <div className="col-md-4 mb-3">
                <label className="form-label">Modificar Tarifas</label>

                <select
                  name="adjustTariffs"
                  className="form-select"
                  value={form.adjustTariffs || "N"}
                  onChange={handleChange}
                  disabled={readOnly}
                >
                  <option value="N">No</option>
                  <option value="Y">Sí</option>
                </select>
              </div>
              {form.adjustTariffs === "Y" && (
                <>
                  <DeviceTariffForm
                    key={"tariffs" + node.id}
                    tariffPlan={form.settlementParams.tariffPlan}
                    data={form.deviceTariffs}
                    readonly={readOnly}
                    onChange={(data: DeviceTariffRow[]) => {

                      const newForm = {
                        ...form,
                        deviceTariffs: data
                      };

                      updateLive(newForm);
                    }}
                  />
                </>
              )}

            </>
          )
        }


        {
          user?.isLafise && !isUserPayfac && user?.banks[0].code == '0051' && (
            <>

              <hr />
              <h6 className="mb-3">Límites del Dispositivo</h6>
              <div className="col-md-4 mb-3">
                <label className="form-label">Modificar Límites</label>

                <select
                  name="adjustLimits"
                  className="form-select"
                  value={form.adjustLimits || "N"}
                  onChange={handleChange}
                  disabled={true}
                >
                  <option value="N">No</option>
                  <option value="Y">Sí</option>
                </select>
              </div>
              {form.adjustLimits === "Y" && (
                <>
                  <LimitsForm
                    limitType="DEVICE"
                    data={form.deviceLimits}
                    readonly={readOnly}
                    onChange={(data) => {

                      const newForm = {
                        ...form,
                        deviceLimits: data
                      };

                      updateLive(newForm);
                    }}
                  />
                </>
              )}

            </>
          )
        }
      </div>

      {!readOnly && (
        <button className="btn btn-primary mt-4" onClick={handleSave}>
          Guardar Terminal
        </button>
      )}

      {errorMessage && (
        <Message
          type="error"
          message={errorMessage}
          closeDialog={removeError}
          handleOk={removeError}
          backgroundColor="rgba(0,0,0,0.4)"
        />
      )}
    </div>
  );
};
